import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    Crown, Users, UserCheck, Activity, TrendingUp, DollarSign,
    Settings, Shield, Bell, FileText, Search, Download, Trash2,
    CheckCircle, XCircle, Clock, AlertTriangle, BarChart3, 
    Eye, Edit, Mail, Calendar, ChevronRight, RefreshCw,
    Database, Server, Zap, Globe, Lock, Unlock, Filter,
    ArrowLeft, UserPlus, Pill, Stethoscope, BookOpen, User
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OwnerPanel() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [providers, setProviders] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    // Verify owner access
    useEffect(() => {
        if (user?.subscription?.plan !== 'owner' && user?.role !== 'owner') {
            toast.error('Access denied. Owner privileges required.');
            navigate('/dashboard');
        }
    }, [user, navigate]);

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [statsRes, usersRes, providersRes] = await Promise.all([
                axios.get(`${API}/owner/stats`),
                axios.get(`${API}/owner/users`),
                axios.get(`${API}/owner/providers`)
            ]);
            setStats(statsRes.data);
            setUsers(usersRes.data);
            setProviders(providersRes.data);
        } catch (err) {
            console.error('Failed to fetch data:', err);
            // If endpoints don't exist yet, use placeholder data
            setStats({
                users: { total: 0, active: 0, premium: 0, new_this_week: 0 },
                providers: { total: 0, pending: 0, approved: 0, suspended: 0 },
                revenue: { total: 0, this_month: 0, subscriptions: 0 },
                assessments: { total: 0, this_week: 0 },
                referrals: { total: 0, pending: 0 },
                prescriptions: { total: 0, pending: 0 }
            });
        } finally {
            setLoading(false);
        }
    };

    const handleApproveProvider = async (providerId) => {
        try {
            await axios.post(`${API}/owner/providers/${providerId}/approve`);
            toast.success('Provider approved successfully');
            fetchAllData();
        } catch (err) {
            toast.error('Failed to approve provider');
        }
    };

    const handleSuspendProvider = async (providerId) => {
        try {
            await axios.post(`${API}/owner/providers/${providerId}/suspend`);
            toast.success('Provider suspended');
            fetchAllData();
        } catch (err) {
            toast.error('Failed to suspend provider');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            await axios.delete(`${API}/owner/users/${userId}`);
            toast.success('User deleted');
            fetchAllData();
        } catch (err) {
            toast.error('Failed to delete user');
        }
    };

    const tabs = [
        { id: 'overview', label: 'Overview', icon: BarChart3 },
        { id: 'users', label: 'Users', icon: Users },
        { id: 'providers', label: 'Providers', icon: UserCheck },
        { id: 'referrals', label: 'Referrals', icon: UserPlus },
        { id: 'prescriptions', label: 'Prescriptions', icon: Pill },
        { id: 'content', label: 'Content', icon: BookOpen },
        { id: 'settings', label: 'Settings', icon: Settings },
    ];

    const filteredUsers = users.filter(u => {
        const matchesSearch = u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             u.name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterStatus === 'all' || 
                             (filterStatus === 'premium' && u.subscription) ||
                             (filterStatus === 'free' && !u.subscription);
        return matchesSearch && matchesFilter;
    });

    const filteredProviders = providers.filter(p => {
        const matchesSearch = p.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             p.name?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterStatus === 'all' || p.status === filterStatus;
        return matchesSearch && matchesFilter;
    });

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Button 
                                variant="ghost" 
                                className="text-white hover:bg-white/10"
                                onClick={() => navigate('/dashboard')}
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back to App
                            </Button>
                            <div className="h-8 w-px bg-white/20" />
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                                    <Crown className="w-6 h-6" />
                                </div>
                                <div>
                                    <h1 className="text-2xl font-bold">Owner Control Panel</h1>
                                    <p className="text-purple-200 text-sm">Full platform management</p>
                                </div>
                            </div>
                        </div>
                        <Button 
                            variant="outline" 
                            className="border-white/30 text-white hover:bg-white/10"
                            onClick={fetchAllData}
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Refresh Data
                        </Button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Tab Navigation */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap
                                    ${activeTab === tab.id 
                                        ? 'bg-primary text-white shadow-lg' 
                                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                            >
                                <Icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="space-y-6">
                        {/* Stats Grid */}
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-blue-100 text-sm">Total Users</p>
                                            <p className="text-3xl font-bold">{stats?.users?.total || 0}</p>
                                            <p className="text-blue-200 text-xs mt-1">+{stats?.users?.new_this_week || 0} this week</p>
                                        </div>
                                        <Users className="w-10 h-10 text-blue-200" />
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-teal-100 text-sm">MediProviders</p>
                                            <p className="text-3xl font-bold">{stats?.providers?.total || 0}</p>
                                            <p className="text-teal-200 text-xs mt-1">{stats?.providers?.pending || 0} pending review</p>
                                        </div>
                                        <UserCheck className="w-10 h-10 text-teal-200" />
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-green-100 text-sm">Revenue</p>
                                            <p className="text-3xl font-bold">${stats?.revenue?.total || 0}</p>
                                            <p className="text-green-200 text-xs mt-1">${stats?.revenue?.this_month || 0} this month</p>
                                        </div>
                                        <DollarSign className="w-10 h-10 text-green-200" />
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-purple-100 text-sm">Assessments</p>
                                            <p className="text-3xl font-bold">{stats?.assessments?.total || 0}</p>
                                            <p className="text-purple-200 text-xs mt-1">{stats?.assessments?.this_week || 0} this week</p>
                                        </div>
                                        <Activity className="w-10 h-10 text-purple-200" />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Quick Actions & Activity */}
                        <div className="grid lg:grid-cols-2 gap-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Zap className="w-5 h-5 text-amber-500" />
                                        Quick Actions
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <Button 
                                        className="w-full justify-start" 
                                        variant="outline"
                                        onClick={() => setActiveTab('providers')}
                                    >
                                        <Clock className="w-4 h-4 mr-2 text-amber-500" />
                                        Review Pending Providers ({stats?.providers?.pending || 0})
                                        <ChevronRight className="w-4 h-4 ml-auto" />
                                    </Button>
                                    <Button 
                                        className="w-full justify-start" 
                                        variant="outline"
                                        onClick={() => setActiveTab('referrals')}
                                    >
                                        <UserPlus className="w-4 h-4 mr-2 text-blue-500" />
                                        Manage Referrals ({stats?.referrals?.total || 0})
                                        <ChevronRight className="w-4 h-4 ml-auto" />
                                    </Button>
                                    <Button 
                                        className="w-full justify-start" 
                                        variant="outline"
                                        onClick={() => setActiveTab('prescriptions')}
                                    >
                                        <Pill className="w-4 h-4 mr-2 text-green-500" />
                                        Review Prescriptions ({stats?.prescriptions?.pending || 0})
                                        <ChevronRight className="w-4 h-4 ml-auto" />
                                    </Button>
                                    <Button 
                                        className="w-full justify-start" 
                                        variant="outline"
                                        onClick={() => setActiveTab('content')}
                                    >
                                        <BookOpen className="w-4 h-4 mr-2 text-purple-500" />
                                        Manage Health Library
                                        <ChevronRight className="w-4 h-4 ml-auto" />
                                    </Button>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <TrendingUp className="w-5 h-5 text-green-500" />
                                        Platform Health
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <Server className="w-5 h-5 text-green-500" />
                                            <span>API Server</span>
                                        </div>
                                        <Badge className="bg-green-500">Online</Badge>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <Database className="w-5 h-5 text-green-500" />
                                            <span>Database</span>
                                        </div>
                                        <Badge className="bg-green-500">Connected</Badge>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <Globe className="w-5 h-5 text-green-500" />
                                            <span>Custom Domain</span>
                                        </div>
                                        <Badge className="bg-green-500">Active</Badge>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                    <div className="space-y-4">
                        <Card>
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <CardTitle>User Management</CardTitle>
                                    <div className="flex gap-2">
                                        <div className="relative">
                                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <Input 
                                                placeholder="Search users..." 
                                                className="pl-9 w-64"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                                            <SelectTrigger className="w-32">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Users</SelectItem>
                                                <SelectItem value="premium">Premium</SelectItem>
                                                <SelectItem value="free">Free</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {filteredUsers.length === 0 ? (
                                        <p className="text-center py-8 text-muted-foreground">No users found</p>
                                    ) : (
                                        filteredUsers.map(u => (
                                            <div key={u.id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                                        <User className="w-5 h-5 text-primary" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{u.name || 'No name'}</p>
                                                        <p className="text-sm text-muted-foreground">{u.email}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    {u.subscription ? (
                                                        <Badge className="bg-purple-500">{u.subscription.plan || 'Premium'}</Badge>
                                                    ) : (
                                                        <Badge variant="outline">Free</Badge>
                                                    )}
                                                    <Button size="sm" variant="ghost">
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="ghost" 
                                                        className="text-red-500"
                                                        onClick={() => handleDeleteUser(u.id)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Providers Tab */}
                {activeTab === 'providers' && (
                    <div className="space-y-4">
                        <Card>
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <CardTitle>Provider Management</CardTitle>
                                    <div className="flex gap-2">
                                        <div className="relative">
                                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <Input 
                                                placeholder="Search providers..." 
                                                className="pl-9 w-64"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                                            <SelectTrigger className="w-40">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Status</SelectItem>
                                                <SelectItem value="pending_review">Pending Review</SelectItem>
                                                <SelectItem value="approved">Approved</SelectItem>
                                                <SelectItem value="suspended">Suspended</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {filteredProviders.length === 0 ? (
                                        <p className="text-center py-8 text-muted-foreground">No providers found</p>
                                    ) : (
                                        filteredProviders.map(p => (
                                            <div key={p.id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-full bg-teal-500/10 flex items-center justify-center">
                                                        <UserCheck className="w-5 h-5 text-teal-500" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{p.name}</p>
                                                        <p className="text-sm text-muted-foreground">{p.email}</p>
                                                        <p className="text-xs text-muted-foreground capitalize">{p.profession}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <Badge className={
                                                        p.status === 'approved' ? 'bg-green-500' :
                                                        p.status === 'pending_review' ? 'bg-amber-500' :
                                                        p.status === 'suspended' ? 'bg-red-500' : 'bg-slate-500'
                                                    }>
                                                        {p.status?.replace('_', ' ')}
                                                    </Badge>
                                                    {p.status === 'pending_review' && (
                                                        <Button 
                                                            size="sm" 
                                                            className="bg-green-500 hover:bg-green-600"
                                                            onClick={() => handleApproveProvider(p.id)}
                                                        >
                                                            <CheckCircle className="w-4 h-4 mr-1" />
                                                            Approve
                                                        </Button>
                                                    )}
                                                    {p.status === 'approved' && (
                                                        <Button 
                                                            size="sm" 
                                                            variant="outline"
                                                            className="text-red-500 border-red-500"
                                                            onClick={() => handleSuspendProvider(p.id)}
                                                        >
                                                            <XCircle className="w-4 h-4 mr-1" />
                                                            Suspend
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Referrals Tab */}
                {activeTab === 'referrals' && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Referral Management</CardTitle>
                            <CardDescription>Review and manage specialist referral requests</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p className="text-center py-8 text-muted-foreground">
                                Referral management coming soon. Users can currently submit referrals through their dashboard.
                            </p>
                        </CardContent>
                    </Card>
                )}

                {/* Prescriptions Tab */}
                {activeTab === 'prescriptions' && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Prescription Management</CardTitle>
                            <CardDescription>Review and approve prescription requests</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p className="text-center py-8 text-muted-foreground">
                                Prescription management coming soon. Users can currently submit requests through their dashboard.
                            </p>
                        </CardContent>
                    </Card>
                )}

                {/* Content Tab */}
                {activeTab === 'content' && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Content Management</CardTitle>
                            <CardDescription>Manage health library articles and educational content</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <p className="text-center py-8 text-muted-foreground">
                                Content management system coming soon. You'll be able to add, edit, and organize health articles.
                            </p>
                        </CardContent>
                    </Card>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Platform Settings</CardTitle>
                                <CardDescription>Configure your MediGuide platform</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="grid gap-4">
                                    <div className="flex items-center justify-between p-4 border rounded-lg">
                                        <div>
                                            <p className="font-medium">Free Trial Checks</p>
                                            <p className="text-sm text-muted-foreground">Number of free symptom checks for new users</p>
                                        </div>
                                        <Badge>3 checks</Badge>
                                    </div>
                                    <div className="flex items-center justify-between p-4 border rounded-lg">
                                        <div>
                                            <p className="font-medium">Premium Features</p>
                                            <p className="text-sm text-muted-foreground">Referrals, Prescriptions, Consultations</p>
                                        </div>
                                        <Badge className="bg-green-500">Enabled</Badge>
                                    </div>
                                    <div className="flex items-center justify-between p-4 border rounded-lg">
                                        <div>
                                            <p className="font-medium">Provider Commission</p>
                                            <p className="text-sm text-muted-foreground">Default commission rate for trial providers</p>
                                        </div>
                                        <Badge>20%</Badge>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Admin Access</CardTitle>
                                <CardDescription>Direct access to admin dashboard</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Button onClick={() => navigate('/admin/dashboard')}>
                                    <Shield className="w-4 h-4 mr-2" />
                                    Open Admin Dashboard
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>
        </div>
    );
}
